import { IObstetricaLabResultsGroup } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { Locale } from "@/Common/Models/Localization/Locale";
import { Patient } from "@/Common/Models/Patient/Patient";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { HeightUnit, WeightUnit } from "@/Common/Utilities/Units/Units";
import { ObstetricalCounter, ObstetricalCountersComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalCountersComponent";
import { ObstetricalFileField, ObstetricalFileRadioButtonField } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/ObstetricalFileField";
import { ObstetricalFileControlRenderer } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRenderer";
import { ClickableTextRenderOptions } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRendererOptions";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { BaseObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileForm";
import { BaseObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileModel";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileMedicalSummary } from "@/Patient/Models/ObstetricalFile/ObstetricalFileMedicalSummary";
import { ObstetricalFileVitalSign } from "@/Patient/Models/ObstetricalFile/ObstetricalFileVitalSign";

interface IObstetricalFilePageProps<TForm> {
    form: TForm;
    contributionId: string;
    patient: Patient;
    encounter: Encounter;
    previousObstetricalFile: BaseObstetricalFileModel;
    obstetricalFileMedicalSummary: ObstetricalFileMedicalSummary;
    consultationDateAtStartOfObstetricalFile: Date;
    isPageHeaderExtended?: boolean;
    contributionCreationDate?: Date;
    contributionType?: ObstetricalFileContributionType;
    readonly?: boolean;
    fetchLabObservations: () => Promise<IObstetricaLabResultsGroup[]>;

    onHeaderToggle?: () => void;
    onFormUpdate: (form: TForm) => void;
    onObstetricalCountersUpdate?: (form: TForm) => void;
    onObstetricalFileVitalSignLink?: (form: TForm, emrTemplateId: string) => void;
    onMedicalItemVitalSignUpdate?: (obstetricalFileVitalSign: ObstetricalFileVitalSign, emrTemplateId: string) => Promise<MedicalItemVitalSign>;
    onPageUnmount: () => void;
}

abstract class ObstetricalFilePageState<TForm extends BaseObstetricalFileForm> {
    public form: TForm;
    public currentClickableTextId: string;
}

abstract class ObstetricalFilePage<TForm extends BaseObstetricalFileForm, TState extends ObstetricalFilePageState<TForm>> extends BaseReactComponent<IObstetricalFilePageProps<TForm>, TState> {
    protected controlRenderer: ObstetricalFileControlRenderer;
    protected get locale(): Locale { return null; }

    public componentWillMount(): void {
        this.setControlRenderer(this.props.readonly);
        this.initState(this.props);
    }

    public componentWillReceiveProps(nextProps: IObstetricalFilePageProps<TForm>): void {
        if (this.props.readonly !== nextProps.readonly
            || this.props.contributionId !== nextProps.contributionId
            || this.props.patient.IndividualResponse.Contact_id != nextProps.patient.IndividualResponse.Contact_id) {
            this.setControlRenderer(nextProps.readonly);
            this.initState(nextProps);
        }
    }

    private setControlRenderer(isReadonly: boolean): void {
        this.controlRenderer = new ObstetricalFileControlRenderer({
            readonly: isReadonly,
            onBlur: () => this.handleFormUpdate(),
            locale: this.locale
        });
    }

    public componentDidMount(): void {
        window.addEventListener("click", this.handleOnWindowClick);
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        window.removeEventListener("click", this.handleOnWindowClick);
        this.props.onPageUnmount();
    }

    private handleOnWindowClick = (): void => {
        this.mutateState(s => s.currentClickableTextId = null);
    };

    public render(): JSX.Element {
        return (
            <div className="page">
                {this.renderPageContent()}
            </div>
        );
    }

    protected abstract initState(props: IObstetricalFilePageProps<TForm>): void;

    protected abstract renderPageContent(): JSX.Element;

    protected renderField(label: string, field: React.ReactNode, className?: string, unit?: string, customLabel?: string): JSX.Element {
        return (
            <ObstetricalFileField
                label={label}
                field={field}
                className={className}
                unit={unit}
                locale={this.locale}
                customLabel={customLabel}
            />
        );
    }

    protected renderRadioButtonField(label: string, field: React.ReactNode, className?: string): JSX.Element {
        return (
            <ObstetricalFileRadioButtonField
                label={label}
                field={field}
                className={className}
                locale={this.locale}
            />
        );
    }

    protected handleFormUpdate(): void {
        this.props.onFormUpdate?.(this.state.form);
    }

    protected handleObstetricalCountersUpdate(): void {
        this.props.onObstetricalCountersUpdate?.(this.state.form);
    }

    protected renderObstetricalCounters(
        colClassName: string,
        renderAsField: boolean = true,
        renderHeaders: boolean = false,
        includeFields: ObstetricalCounter[] = [],
        showAlternateAbortionCounter: boolean = false,
        longLabel?: boolean,
        largeFirstLetterLabels?: boolean,
        alternateLabels?: { top?: { [index: string]: string }, side?: { [index: string]: string } },
        locale?: Locale
    ): JSX.Element {
        const counterComponent = (
            <ObstetricalCountersComponent
                containerId="obstetrical-counter"
                readonly={this.props.readonly}
                renderHeaders={renderHeaders}
                obstetricalCounters={this.state.form.ObstetricalCounters}
                includeFields={includeFields}
                onChange={obstetricalCounters => this.mutateState(s => s.form.ObstetricalCounters = obstetricalCounters)}
                onBlur={() => this.handleObstetricalCountersUpdate()}
                showAlternateAbortionCounter={showAlternateAbortionCounter}
                longLabel={longLabel}
                largeFirstLetterLabels={largeFirstLetterLabels}
                alternateLabels={alternateLabels}
                locale={locale}
            />
        );

        if (renderAsField)
            return this.renderField(null, counterComponent, colClassName);

        return (<div className={colClassName}>{counterComponent}</div>);
    }

    protected handleObstetricalFileVitalSignLink(emrTemplateId: string): void {
        this.props.onObstetricalFileVitalSignLink?.(this.state.form, emrTemplateId);
    }

    protected async handleVitalSignUpdate(obstetricalFileVitalSign: ObstetricalFileVitalSign, emrTemplateId: string): Promise<MedicalItemVitalSign> {
        return await this.props.onMedicalItemVitalSignUpdate?.(obstetricalFileVitalSign, emrTemplateId);
    }

    protected getWeightBeforePregnancyDropdownOptions(unit: WeightUnit = WeightUnit.Kilogram): SearchableItemModelT<string>[] {
        return this.props.obstetricalFileMedicalSummary.getThreeLatestVitalSignsWithWeight(this.props.consultationDateAtStartOfObstetricalFile)
            .map(p => {
                const vitalSign = new MedicalItemVitalSign(p);
                const roundedWeight = vitalSign.getWeightIn(unit)?.toFixed(1);
                return new SearchableItemModelT<string>(`${roundedWeight} ${WeightUnit[unit].toLowerCase()} (${new DateOnly(p.PharmaStartDate).toString()})`, roundedWeight);
            });
    }

    protected getHeightDropdownOptions(unit: HeightUnit = HeightUnit.Meter, isInCentimeter?: boolean): SearchableItemModelT<string>[] {
        return this.props.obstetricalFileMedicalSummary.getThreeLatestVitalSignsWithHeight(this.props.consultationDateAtStartOfObstetricalFile)
            .map(p => {
                const vitalSign = new MedicalItemVitalSign(p);
                const roundedHeight = isInCentimeter
                    ? vitalSign.getHeightInCentimeters()?.toFixed(2)
                    : vitalSign.getHeightIn(unit)?.toFixed(2);

                return new SearchableItemModelT<string>(`${roundedHeight} ${isInCentimeter ? "cm" : HeightUnit[unit].toLowerCase()} (${new DateOnly(p.PharmaStartDate).toString()})`, roundedHeight);
            });
    }

    protected getClickableTextOptions(id: string, context: string, leftAlign?: boolean): ClickableTextRenderOptions {
        return {
            show: this.state.currentClickableTextId === id,
            onClick: show => this.mutateState(s => s.currentClickableTextId = show ? id : null),
            context: context,
            leftAlign: leftAlign
        };
    }
}

export { IObstetricalFilePageProps, ObstetricalFilePage, ObstetricalFilePageState };
